// Axios
import * as Sentry from '@sentry/browser';
import axios, { AxiosStatic } from 'axios';
import * as DataTable from 'datatables.net-dt';
// jQuery
import jQuery from 'jquery';

// Laravel Echo
import Echo from 'laravel-echo';
import * as select2 from 'select2';
// socket.io-client 2.5.0 is CJS-only (`module.exports = lookup`); the default import is that
// callable, matching what `require('socket.io-client')` returned before.
import socketIoClient from 'socket.io-client';

// Captcha globals
import { initializeCaptchaGlobals } from '../utils/captchaWindowGlobal';
import { collectAttributionSnapshot } from '../utils/tracking/attributionSnapshot';

import { installGlobalClientErrorHandlers } from './reportClientError';

import '../initSentry';

// Bootstrap ($-plugins; must be evaluated after jQuery, which the import order above guarantees)
import 'bootstrap-datepicker';
import 'bootstrap';

// Capture otherwise-invisible frontend failures (rejected promises, runtime errors).
installGlobalClientErrorHandlers();

collectAttributionSnapshot();

const $: jQueryInterface = jQuery as unknown as jQueryInterface;

$.DataTable = DataTable;
$.select2 = select2;

// select2 ships a UMD wrapper whose CommonJS branch exports a FACTORY — `module.exports =
// function (root, jQuery) { … }` — instead of registering itself. Importing it therefore attaches
// nothing to jQuery on its own; webpack happened to hide that, Rolldown does not, and the symptom
// is `$(…).select2 is not a function` on any page with a styled select.
// Calling the factory is the documented way to consume that build, and the guard keeps it correct
// under either bundler: if the plugin is already attached, this is a no-op.
if (typeof ($.fn as unknown as { select2?: unknown }).select2 !== 'function') {
    const registerSelect2 = (select2 as unknown as { default?: unknown }).default ?? select2;

    if (typeof registerSelect2 === 'function') {
        (registerSelect2 as (root: unknown, jq: unknown) => void)(window, $);
    }
}

$.fn.extend({
    freshData(key, value) {
        // This function returns parsed fresh values of data-* attributes.
        // jQuery.data function caches every element's data-* attribute value
        // and thus it is unable to fetch updated values after changes in
        // the DOM have been made.
        // jQuery.attr on the other hand returns unparsed strings.
        if (typeof this === 'undefined' || !this.length) {
            return false;
        }

        const elem = this[0];
        if (typeof value !== 'undefined') {
            $(elem).attr(`data-${key}`, value);
        }

        try {
            return JSON.parse($(elem).attr(`data-${key}`));
        } catch (e) {
            return $(elem).attr(`data-${key}`);
        }
    },
});

window.$ = window.jQuery = $;

axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
window.axios = axios;

const token = document.head.querySelector<HTMLMetaElement>('meta[name="csrf-token"]');
if (token) {
    window.axios.defaults.headers.common['X-CSRF-TOKEN'] = token.content;
}

const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 1000;
const retryCounts = new Map();

// WARNING! TEMPORARY CODE TO GENERATE NEW LINKS IN ORDER TO OMIT BROWSER CACHE!
axios.interceptors.request.use((config) => {
    const url = new URL(config.url!, window.location.origin);
    // tylko jeśli nie ma jeszcze fx8=77b
    if (!url.searchParams.has('fx8')) {
        url.searchParams.set('fx8', '77b');
    }
    config.url = url.pathname + url.search; // zachowaj względny URL
    return config;
});
// WARNING! TEMPORARY CODE TO GENERATE NEW LINKS IN ORDER TO OMIT BROWSER CACHE!

axios.interceptors.response.use(undefined, async (error) => {
    const config = error.config;

    if (!config || !config.url || axios.isCancel(error)) return Promise.reject(error);

    const retryKey = `${config.method ?? 'get'}|${config.url}`;
    const currentRetry = retryCounts.get(retryKey) || 0;

    if (currentRetry < MAX_RETRIES && (!error.response || error.code === 'ECONNABORTED')) {
        retryCounts.set(retryKey, currentRetry + 1);
        await new Promise((res) => setTimeout(res, RETRY_DELAY_MS));
        return axios(config);
    }

    retryCounts.delete(retryKey);

    if (error.response && ![401, 404, 422].includes(error.response.status)) {
        Sentry.captureException(error, {
            tags: {
                axios: 'global',
                method: config.method ?? 'unknown',
                url: config.url ?? 'unknown',
                retryCount: String(currentRetry),
            },
            extra: { config },
        });
    }

    return Promise.reject(error);
});

if (process.env.MIX_NOTIFICATIONS_ENABLED === 'true') {
    // NOTE: `process.env.*` is a RUNTIME lookup here (webpack ProvidePlugin -> processEnvRuntime.js),
    // so webpack never dead-code-eliminated this branch: socket.io-client was always part of the
    // bundle. Hoisting it to a static import therefore keeps bundle contents identical; only the
    // module-evaluation moment moves earlier (the lib has no load-time side effects).
    // F2 will turn this into a dynamic import behind the runtime flag (see plan §F2.3).
    window.io = socketIoClient;

    if (typeof window.io !== 'undefined') {
        const port = process.env.MIX_NOTIFICATIONS_PORT ?? '6001';
        const portColon = port?.length ? ':' : '';

        window.Echo = new Echo({
            broadcaster: 'socket.io',
            host: `${window.location.hostname}${portColon}${port}`,
            encrypted: true,
        });
    }
}

// Globals
declare global {
    interface Window {
        $: jQueryInterface;
        jQuery: jQueryInterface;
        axios: AxiosStatic;
        Echo: InstanceType<typeof Echo>;
        io: object;
        dataLayer: any[];
        fbq: (...args: any[]) => void;
        srtmCommands: any[];
        SportRadarEvents: {};
        handleReCaptchaChange: (captchaToken: string) => void;
        pwaInstallEvent: (Event & { prompt(): Promise<void>; userChoice: Promise<{ outcome: string }> }) | null;
    }

    interface jQueryInterface extends JQueryStatic {
        select2?: any;
        DataTable?: any;
        freshData: any;
    }

    interface JQuery {
        datepicker: (...args: any) => any;
        slick(...args: any): JQuery;
    }
}

$('.hide-before-load').removeClass('hide-before-load');

// Initialize captcha globals for blade compatibility
initializeCaptchaGlobals();

// Exports
export { axios, $ };
